<script lang="ts">
    import { onDestroy, onMount, tick } from 'svelte'
    import { fade } from 'svelte/transition'

    import Icon from '@iconify/svelte'
    import { cloneDeep, isEqual } from 'lodash-es'
    import * as yup from 'yup'

    import { gettext as _, ngettext } from 'hydria/services/gettext'
    import { asAny } from 'hydria/svelte_helpers'

    import type { TldConfig } from './defs'

    import InputIntCurrency from 'hydria/components/InputIntCurrency.svelte'

    interface Props {
        userId?: string | null
        tldConfig: TldConfig
        close: () => void
        save: (v) => void
    }

    let { userId = null, tldConfig, close: onClose, save: onSave }: Props = $props()
    let form = $state(cloneDeep(tldConfig))
    let currentYears = $derived(new Set(form.prices.map((p) => p.n_years)))
    let nYearsToAdd: number | null = $state(null)

    function addYear() {
        const n = nYearsToAdd as number
        nYearsToAdd = null
        form.prices.push({ n_years: n, price: 0 })
        form.prices.sort((a, b) => a.n_years - b.n_years)
        form.prices = form.prices

        tick().then(() => {
            const el = document.getElementById(`tlde-p-${n}`) as HTMLInputElement
            el.focus()
            el.select()
        })
    }

    let changed = $derived(!isEqual(form, tldConfig))
    // Helper function to simplify that try/catch bullshit when we only want a boolean
    function validateSyncAt(key: string, form: TldConfig) {
        try {
            schema.validateSyncAt(key, form)
            return true
        } catch {
            return false
        }
    }

    const ypSchema = yup.number().min(1).max(500000).required()

    const shape = {
        prices: yup.array().of(
            yup.object().shape({
                n_years: yup.number().min(1).max(10).required(),
                price: ypSchema.clone(),
            }),
        ),
    }
    if (!userId) {
        Object.assign(shape, {
            in_lookup: yup.boolean(),
            whois_allowed: yup.boolean(),
            whois_price: yup
                .number()
                .test('wprice', '{path} is not a valid', (value) => value == null || value >= 0)
                .nullable(),
            idna_languages: yup.array().of(yup.string()),
        })
    }
    const schema = yup.object().shape(shape)

    let formIsValid = $derived(schema.isValidSync(form))
    // $: in_lookupIsValid = !userId && validateSyncAt('in_lookup', form)
    // $: whois_allowedIsValid = !userId && validateSyncAt('whois_allowed', form)
    let whois_priceIsValid = $derived(!!userId || validateSyncAt('whois_price', form))
    // $: pricesIsValid = validateSyncAt('prices', form)

    onMount(() => {
        document.documentElement.classList.add('modal-open')
    })
    onDestroy(() => {
        document.documentElement.classList.remove('modal-open')
    })

    function close() {
        onClose()
    }
    function save() {
        Object.assign(tldConfig, form)
        onSave(tldConfig)
    }
</script>

<svelte:window
    onkeydown={(e) => {
        if (e.key === 'Escape') {
            close()
        }
    }}
/>

<div class="modal" role="dialog" transition:fade={{ duration: 250 }}>
    <div class="modal-dialog">
        <div class="modal-content">
            <div class="modal-header text-bg-primary">
                <div>TLD <span class="text-muted">.</span>{tldConfig.tld}</div>
            </div>
            <div class="modal-body">
                <div class="d-grid gap-2">
                    <div class="row">
                        <label for="tlde-tld" class="col-sm-4 col-form-label">TLD</label>
                        <div class="col-sm-8">
                            <p class="form-control-static">.{form.tld}</p>
                        </div>
                    </div>

                    {#if !userId}
                        <div class="row">
                            <label for="tlde-wallowed" class="col-sm-4 col-form-label">WHOIS Privacy</label>
                            <div class="col-sm-8">
                                <div class="radio">
                                    <label>
                                        <input type="radio" name="tlde-wallowed" bind:group={form.whois_allowed} value={false} /> N’est pas permis
                                    </label>
                                </div>
                                <div class="radio">
                                    <label>
                                        <input type="radio" name="tlde-wallowed" bind:group={form.whois_allowed} value={true} style="margin-top: 7px;" /> Permis à ce prix :
                                        <div style="display: inline-block; width: 9em; vertical-align: middle;">
                                            <div class="input-group input-group-sm">
                                                <InputIntCurrency class="form-control input-group-sm {whois_priceIsValid ? '' : 'is-invalid'}" bind:value={form.whois_price} min={0} max={10000} />
                                                <span class="input-group-text">$</span>
                                            </div>
                                        </div>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div class="row">
                            <label for="tlde-lookup" class="col-sm-4 col-form-label">{$_('Présence dans la recherche')}</label>
                            <div class="col-sm-8">
                                <div class="radio">
                                    <label>
                                        <input type="radio" name="tlde-lookup" bind:group={form.in_lookup} value={false} />
                                        {$_('Uniquement si recherché « domaine.{tld} »', { tld: tldConfig.tld })} ;
                                    </label>
                                </div>
                                <div class="radio">
                                    <label>
                                        <input type="radio" name="tlde-lookup" bind:group={form.in_lookup} value={true} />
                                        {$_('Toujours présent')}.<br />
                                        <span class="text-muted">{$_('Le temps de recherche OpenSRS est directement proportionnel au nombre de TLD configurés ainsi.')}</span>
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div class="row">
                            <label for="tlde-idna" class="col-sm-4 col-form-label">{$_('IDNA')}</label>
                            <div class="col-sm-8">
                                <div class="form-check">
                                    <input
                                        class="form-check-input"
                                        type="checkbox"
                                        checked={form.idna_languages.includes('fra')}
                                        id="idnalg-fra"
                                        onclick={() => {
                                            if (form.idna_languages.includes('fra')) {
                                                const pos = form.idna_languages.indexOf('fra')
                                                if (pos >= 0) form.idna_languages.splice(pos, 1)
                                            } else {
                                                form.idna_languages.push('fra')
                                            }
                                            form.idna_languages = form.idna_languages
                                        }}
                                    />
                                    <label for="idnalg-fra" class="form-check-label">Français</label>
                                </div>
                            </div>
                        </div>
                    {/if}

                    <form
                        onsubmit={(e) => {
                            e.preventDefault()
                            addYear()
                        }}
                    >
                        <div class="row">
                            <label for="tlde-add-year" class="col-sm-4 col-form-label">{$_('Ajouter un prix')}</label>
                            <div class="col-sm-5">
                                <div class="input-group input-group-sm">
                                    <input type="number" class="form-control" min={1} max={10} step={1} bind:value={nYearsToAdd} class:is-warning={currentYears.has(asAny(nYearsToAdd))} />
                                    <div class="input-group-text">{$ngettext('année', 'années', { n: asAny(nYearsToAdd) })}</div>
                                </div>
                            </div>
                            <div class="col-sm-3">
                                <button type="submit" class="btn btn-sm btn-outline-secondary" class:btn-primary={1 <= asAny(nYearsToAdd) && asAny(nYearsToAdd) <= 10 && !currentYears.has(asAny(nYearsToAdd))} disabled={asAny(nYearsToAdd) < 1 || asAny(nYearsToAdd) > 10 || currentYears.has(asAny(nYearsToAdd))}><Icon inline icon="fa:plus" /></button>
                            </div>
                        </div>
                    </form>

                    {#each form.prices as price}
                        <div class="row">
                            <label for="tlde-p-{price.n_years}" class="col-sm-4 col-form-label">{$ngettext('{n} année', '{n} années', { n: price.n_years })}</label>
                            <div class="col-sm-5">
                                <div class="input-group input-group-sm">
                                    <InputIntCurrency id="tlde-p-{price.n_years}" class="form-control {ypSchema.isValidSync(price.price) ? '' : 'is-invalid'} {nYearsToAdd !== price.n_years ? '' : 'is-warning'}" max={100000} bind:value={price.price} />
                                    <div class="input-group-text">$</div>
                                </div>
                            </div>
                            <div class="col-sm-3">
                                <button
                                    type="button"
                                    class="btn btn-sm btn-outline-secondary"
                                    tabindex="-1"
                                    onclick={() => {
                                        form.prices = form.prices.filter((p) => p !== price)
                                    }}><Icon inline icon="fa:trash" /></button
                                >
                            </div>
                        </div>
                    {/each}
                </div>
            </div>
            <div class="modal-footer">
                <button type="button" class="btn btn-outline-secondary" onclick={() => close()}>{$_('Close')}</button>
                <button type="button" class="btn btn-outline-secondary" onclick={() => save()} disabled={!changed || !formIsValid}>{$_('Save')}</button>
            </div>
        </div>
    </div>
    <div class="modal-backdrop" onclick={() => close()} aria-hidden="true"></div>
</div>
